import React, { useState, useEffect, useContext } from 'react';
import {
    Dialog,
    DialogContent,
    Button,
    Typography,
    Box,
    Slider,
    Tooltip,
    Stack,
    createTheme,
    ThemeProvider,
    SliderThumb,
    ButtonProps,
    FormGroup,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import stakesChipIcon from '/images/table/chip.svg';
import fundsChipIcon from '/images/table/chip-alt.svg';
import styles from '../../../assets/css/components/SitOptionsModal.module.css';
import MenuIcon from '@mui/icons-material/Menu';
import InfoIcon from '@mui/icons-material/Info';
import { isMobile } from 'react-device-detect';
import TimerIcon from '@mui/icons-material/Timer';
import { DetachedWindowContext } from '../WindowPortal';
import { buildHsUrlWithToken } from '../../../Utils/SessionUrlUtils';
import { t } from '../../../i18n';

const darkTheme = createTheme({
    palette: {
        mode: "dark",
        primary: {
            main: "#F93751",
        },
        secondary: {
            main: "#3D3E49",
        },
        info: {
            main: "#ffffff",
        },
    },
    typography: {
        fontFamily: 'Inter, serif',
    },
});

const CustomSlider = styled(Slider)(({}) => ({
    color: '#303136',
    height: 2,
    '& .MuiSlider-thumb': {
        backgroundColor: '#D7D4D4',
        border: '3px solid white',
        borderRadius: '4px',
        color: '#B4B4B4',
        padding: '10px',
        width: '30px',
        height: '36px',
        zIndex: 2
    },
    '& .MuiSlider-track': {
        height: 5,
        color: '#FF004D',
        zIndex: 1
    },
}));

interface CustomSliderThumbComponentProps extends React.HTMLAttributes<unknown> {}

const CustomSliderThumbComponent = (props: CustomSliderThumbComponentProps) => {
    const { children, ...other } = props;
    return (
      <SliderThumb {...other}>
        {children}
        <MenuIcon style={{ transform: 'rotate(90deg)' }}/>
      </SliderThumb>
    );
}

interface CustomButtonProps extends ButtonProps {
    flexGrow?: number;
}

const QuickAmountButton: React.FC<CustomButtonProps> = ({ children, flexGrow = 1, ...props }) => {
    return (
        <Button
        variant="contained"
        color="secondary"
        sx={{
            flexGrow,
            fontSize: isMobile ? "14px" : "clamp(12px, 1.7vh, 14px)",
            fontWeight: 400,
            textTransform: "capitalize",
            lineHeight: "16px",
            height: isMobile ? "32px" : "clamp(30px, 5vh, 38px)",
            minWidth: isMobile ? undefined : "74px"
        }}
        {...props}
        >
        {children}
        </Button>
    );
};

interface CheckboxLabelWithTooltipProps {
    text: string;
    tooltipText: string;
  }

const CheckboxLabelWithTooltip: React.FC<CheckboxLabelWithTooltipProps> = ({ text, tooltipText }) => {
    return (
        <Box display="flex" alignItems="center" gap={1}>
            <Typography>{text}</Typography>
            <Tooltip title={tooltipText} placement="right" arrow>
                <InfoIcon fontSize="small" sx={{ cursor: 'pointer', opacity: '20%' }} />
            </Tooltip>
        </Box>
    );
};

const StyledDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        background: 'radial-gradient(circle, #363844 0%, #2A2B32 92%)',
        color: 'white',
        width: isMobile ? '90%' : 'min(550px, calc(100vw - 32px))',
        maxWidth: isMobile ? '90%' : 'calc(100vw - 32px)',
        maxHeight: isMobile ? 'calc(100dvh - 32px)' : 'calc(100dvh - 24px)',
        padding: '0',
        borderRadius: isMobile ? '30px' : 'clamp(18px, 4vh, 30px)',
        position: 'relative',
        boxShadow: 'inset 0 0 16px 4px rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
    },
    '& .MuiDialogContent-root': {
        boxSizing: 'border-box',
        padding: isMobile ? '30px !important' : 'clamp(16px, 4vh, 40px) clamp(18px, 3vw, 40px) !important',
        maxHeight: isMobile ? 'calc(100dvh - 32px)' : 'calc(100dvh - 24px)',
        overflowY: 'auto',
        overflowX: 'hidden',
    }
});

interface BuyinModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (buyInAmount: number, postOrWaitChoice?: 'post' | 'wait') => void;
    buyInLimits: {
        min: number;
        max: number;
    };
    gameState: {
        isGameActive: boolean;
        requiresChoice: boolean;
        waitForBB?: boolean;
        isHeadsUp?: boolean;
        message?: string;
        postOrWaitOptions?: {
            canPost: boolean;
            postAmount: number;
            postType: string;
            blinds: {
                small: number;
                big: number;
            };
        };
        balanceWarning?: {
            type: 'mustReload' | 'shouldReload' | 'notEnoughBalance';
            currentBalance: number;
            requiredAmount?: number;
            recommendedAmount?: number;
            willSitOut: boolean;
            limits: {
                minBuyIn: number;
                maxBuyIn: number;
            };
        };
    };
    roomName: string;
    smallBlind: number;
    bigBlind: number;
    userBalance: number | string;
    type: string;
    ante?: number;
    sitOptions?: any;
    container?: HTMLElement | null;
    currency?: string;
    currencyType?: 'fiat' | 'crypto' | 'tournament_chips';
    rebuyOfferProp?: { tournamentId: string; chips: number; price: number; secondsToExpire: number; expiresAt?: string } | null;
    addonOfferProp?: { tournamentId: string; endsAt: string; price: number; chips: number; maxPerPlayer: number } | null;
}

const parseTimeString = (timeString: string): number => {
    const [hours, minutes, seconds] = timeString.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
};

const formatTimeDisplay = (hours: number, minutes: number, seconds: number): string => {
    if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`;
    }
    if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
};

const BuyinModal: React.FC<BuyinModalProps> = ({
    open,
    onClose,
    onConfirm,
    buyInLimits,
    gameState,
    roomName,
    type,
    smallBlind,
    bigBlind,
    userBalance,
    ante,
    sitOptions,
    container,
    currency,
    currencyType,
    rebuyOfferProp,
    addonOfferProp
}) => {
    const safeBuyInLimits = buyInLimits || { min: 0, max: 0 };
    const [buyInAmount, setBuyInAmount] = useState(safeBuyInLimits.min);
    const [timeSinceStoodUp, setTimeSinceStoodUp] = useState('');
    const [timeUntilReset, setTimeUntilReset] = useState('00:00:00');
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const [rebuyCountdown, setRebuyCountdown] = useState<number | null>(null);
    const [rebuyOffer, setRebuyOffer] = useState<{ tournamentId: string; chips: number; price: number; secondsToExpire: number; expiresAt?: string } | null>(null);
    const [rebuySubmitting, setRebuySubmitting] = useState(false);
    const detachedWindow = useContext(DetachedWindowContext);
    const [addonOffer, setAddonOffer] = useState<{ tournamentId: string; endsAt: string; price: number; chips: number; maxPerPlayer: number } | null>(null);
    const [addonCountdown, setAddonCountdown] = useState<number | null>(null);
    const [addonSubmitting, setAddonSubmitting] = useState(false);

    useEffect(() => {
        if (!sitOptions?.stoodAt || !sitOptions?.timeUntilNormalBuyinResets) return;

        if (remainingSeconds === 0) {
            setRemainingSeconds(parseTimeString(sitOptions.timeUntilNormalBuyinResets));
        }

        const updateTimes = () => {
            // Calculate time since stood up
            const stoodAtTime = new Date(sitOptions.stoodAt).getTime();
            const currentTime = new Date().getTime();
            const secondsSinceStoodUp = Math.floor((currentTime - stoodAtTime) / 1000);

            const hours = Math.floor(secondsSinceStoodUp / 3600);
            const minutes = Math.floor((secondsSinceStoodUp % 3600) / 60);
            const seconds = secondsSinceStoodUp % 60;

            setTimeSinceStoodUp(formatTimeDisplay(hours, minutes, seconds));

            // Decrement the countdown
            setRemainingSeconds(prev => Math.max(0, prev - 1));
        };

        const interval = setInterval(updateTimes, 1000);
        return () => clearInterval(interval);
    }, [sitOptions?.stoodAt, sitOptions?.timeUntilNormalBuyinResets]);

    // Rebuy offer detection via GameRoom updateRoomState attaching to roomState.sitOptions?.gameState?
    useEffect(() => {
        const maybeRebuy = (rebuyOfferProp as any) || (sitOptions as any)?.rebuyOffer || (gameState as any)?.rebuyOffer;
        if (maybeRebuy && typeof maybeRebuy.secondsToExpire === 'number') {
            setRebuyOffer(maybeRebuy);
            const now = Date.now();
            const expiresAtMs = maybeRebuy.expiresAt ? Date.parse(maybeRebuy.expiresAt) : now + (maybeRebuy.secondsToExpire * 1000);
            const startRemaining = Math.max(0, Math.floor((expiresAtMs - now) / 1000));
            setRebuyCountdown(startRemaining);
        } else {
            setRebuyOffer(null);
            setRebuyCountdown(null);
        }
    }, [rebuyOfferProp, sitOptions, gameState]);

    // Add-on offer detection
    useEffect(() => {
        const maybeAddon = (addonOfferProp as any) || (sitOptions as any)?.addonOffer || (gameState as any)?.addonOffer;
        if (maybeAddon && typeof maybeAddon.endsAt === 'string') {
            setAddonOffer(maybeAddon);
            const now = Date.now();
            const endsAtMs = Date.parse(maybeAddon.endsAt);
            const startRemaining = Math.max(0, Math.floor((endsAtMs - now) / 1000));
            setAddonCountdown(startRemaining);
        } else {
            setAddonOffer(null);
            setAddonCountdown(null);
        }
    }, [addonOfferProp, sitOptions, gameState]);

    // Rebuy countdown tick
    useEffect(() => {
        if (rebuyCountdown === null) return;
        if (rebuyCountdown <= 0) return;
        const timer = setInterval(() => {
            setRebuyCountdown(prev => (prev !== null ? Math.max(0, prev - 1) : null));
        }, 1000);
        return () => clearInterval(timer);
    }, [rebuyCountdown]);

    // Addon countdown tick
    useEffect(() => {
        if (addonCountdown === null) return;
        if (addonCountdown <= 0) return;
        const timer = setInterval(() => {
            setAddonCountdown(prev => (prev !== null ? Math.max(0, prev - 1) : null));
        }, 1000);
        return () => clearInterval(timer);
    }, [addonCountdown]);

    // Auto-close/decline on addon expiry
    useEffect(() => {
        if (!addonOffer) return;
        if (addonCountdown === 0 && !addonSubmitting) {
            setAddonSubmitting(true);
            try {
                (window as any).interactionHandler?.sendTournamentAddonDecline?.(addonOffer.tournamentId);
            } catch {}
            onClose();
        }
    }, [addonCountdown, addonOffer, addonSubmitting]);

    // Auto-decline on expiry
    useEffect(() => {
        if (!rebuyOffer) return;
        if (rebuyCountdown === 0 && !rebuySubmitting) {
            setRebuySubmitting(true);
            try {
                (window as any).interactionHandler?.sendTournamentRebuyDecline?.(rebuyOffer.tournamentId);
            } catch {}
            onClose();
        }
    }, [rebuyCountdown, rebuyOffer, rebuySubmitting]);

    // Update formatted time whenever remainingSeconds changes
    useEffect(() => {
        const hours = Math.floor(remainingSeconds / 3600);
        const minutes = Math.floor((remainingSeconds % 3600) / 60);
        const seconds = remainingSeconds % 60;
        setTimeUntilReset(formatTimeDisplay(hours, minutes, seconds));
    }, [remainingSeconds]);

    const numericBalance = typeof userBalance === 'string' ? parseFloat(userBalance) : userBalance;

    const handleChoice = (choice: 'post' | 'wait') => {
        onConfirm(buyInAmount, choice);
    };

    const handleAddonAccept = () => {
        if (!addonOffer) return;
        setAddonSubmitting(true);
        try {
            (window as any).interactionHandler?.sendTournamentAddonAccept?.(addonOffer.tournamentId);
        } catch {}
        onClose();
    };

    const handleAddonDecline = () => {
        if (!addonOffer) return;
        setAddonSubmitting(true);
        try {
            (window as any).interactionHandler?.sendTournamentAddonDecline?.(addonOffer.tournamentId);
        } catch {}
        onClose();
    };

    const handleRebuyAccept = () => {
        if (!rebuyOffer) return;
        setRebuySubmitting(true);
        try {
            (window as any).interactionHandler?.sendTournamentRebuyAccept?.(rebuyOffer.tournamentId);
        } catch {}
        onClose();
    };

    const handleRebuyDecline = () => {
        if (!rebuyOffer) return;
        setRebuySubmitting(true);
        try {
            (window as any).interactionHandler?.sendTournamentRebuyDecline?.(rebuyOffer.tournamentId);
        } catch {}
        onClose();
    };

    const formatAmount = (amount: number, forceUSD: boolean = false) => {
        if (currencyType === 'tournament_chips' && !forceUSD) {
            return `${Math.floor(amount)}`;
        }

        const valueToFormat = Math.floor(amount * 100) / 100;

        if (forceUSD) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(valueToFormat);
        }

        if (currencyType === 'crypto' && currency) {
            return `${valueToFormat.toFixed(2)} ${currency.toUpperCase()}`;
        }

        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(valueToFormat);
    };
    const formatRebuyPrice = (price: number) => {
        const fiatCurrency = sitOptions?.currency || currency || 'USD';
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: fiatCurrency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(Math.floor(price * 100) / 100);
        } catch {
            return `$${(Math.floor(price * 100) / 100).toFixed(2)}`;
        }
    };

    const renderInfoBox = () => {
        if (rebuyOffer) {
            const disabled = (rebuyCountdown ?? 0) <= 0;
            const minutes = Math.floor((rebuyCountdown ?? 0) / 60).toString().padStart(2, '0');
            const seconds = ((rebuyCountdown ?? 0) % 60).toString().padStart(2, '0');
            return (
                <Box
                    sx={{
                        mt: 2,
                        color: '#1BE958',
                        backgroundColor: 'rgba(27, 233, 88, 0.08)',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(27, 233, 88, 0.25)'
                    }}
                >
                    <Typography
                        variant="body2"
                        align="center"
                        sx={{ fontSize: '0.95rem', mb: 1, fontWeight: 600 }}
                    >
                        {t('BuyInModal:rebuy.available')}
                    </Typography>
                    <Typography variant="body2" align="center" sx={{ mb: 1 }}>
                        {t('BuyInModal:rebuy.question', { chips: Math.floor(rebuyOffer.chips), price: `$${rebuyOffer.price.toFixed(2)}` })}
                    </Typography>
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ mt: 1 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleRebuyAccept}
                            disabled={disabled}
                            sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                        >
                            {t('BuyInModal:rebuy.accept')}
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleRebuyDecline}
                            sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                        >
                            {t('BuyInModal:rebuy.decline')}
                        </Button>
                    </Stack>
                    <Typography variant="caption" display="block" align="center" sx={{ mt: 1, opacity: 0.8 }}>
                        {t('BuyInModal:rebuy.expiresIn', { time: `${minutes}:${seconds}` })}
                    </Typography>
                </Box>
            );
        }
        if (gameState.balanceWarning) {
            if (gameState.balanceWarning.type === 'notEnoughBalance') {
                return (
                    <Box
                        sx={{
                            mt: 2,
                            color: '#ff3d00',
                            backgroundColor: 'rgba(255, 61, 0, 0.1)',
                            padding: '16px',
                            borderRadius: '4px',
                        }}
                    >
                        <Typography
                            variant="body2"
                            align="center"
                            sx={{
                                fontSize: '0.875rem',
                                mb: 1
                            }}
                        >
                            {t('BuyInModal:balanceWarning.insufficient')}
                        </Typography>
                        <Typography
                            variant="body2"
                            align="center"
                            sx={{
                                fontSize: '0.875rem'
                            }}
                        >
                            {t('BuyInModal:balanceWarning.addFundsMessage')}
                        </Typography>
                    </Box>
                );
            }
            return null;
        }

        if (gameState.balanceWarning) return null;

        if (type === 'aof' &&
            sitOptions?.stoodAt &&
            sitOptions?.normalBuyinResetAt &&
            sitOptions?.timeUntilNormalBuyinResets) {

            return (
                <Box
                    sx={{
                        mt: 2,
                        color: '#ff9800',
                        backgroundColor: 'rgba(255, 152, 0, 0.1)',
                        padding: '16px',
                        borderRadius: '4px',
                    }}
                >
                    <Typography
                        variant="body2"
                        align="center"
                        sx={{
                            fontSize: '0.875rem',
                            mb: 2
                        }}
                    >
                        {t('BuyInModal:gameIntegrity.message')}
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={3}
                        justifyContent="center"
                        sx={{
                            '& .MuiSvgIcon-root': {
                                fontSize: '1.2rem',
                                opacity: 0.7,
                                mr: 1
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TimerIcon />
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {t('BuyInModal:gameIntegrity.resetIn')}{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        background: 'linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                                        backdropFilter: 'blur(4px)',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                    }}
                                >
                                    {timeUntilReset}
                                </Box>
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
            );
        }

        return (
            <Typography
                align="center"
                variant="body2"
                sx={{
                    my: 2,
                    opacity: "75%",
                    color: gameState.message ? '#3498db' : 'inherit'
                }}
            >
                {type === 'aof' ? (
                    t('BuyInModal:gameStatus.addedNextHand')
                ) : (
                    <>
                        {gameState.waitForBB === true ? (
                            t('BuyInModal:gameStatus.playNextBB')
                        ) : gameState.isGameActive ? (
                            gameState.requiresChoice ? (
                                t('BuyInModal:gameStatus.postOrWait')
                            ) : (
                                t('BuyInModal:gameStatus.playNextHand')
                            )
                        ) : (
                            gameState.waitForBB === false ? t('BuyInModal:gameStatus.waitingForPlayers') : t('BuyInModal:gameStatus.waitingForPlayers')
                        )}
                    </>
                )}
            </Typography>
        );
    };

    // Use the detached window context if available to render in the correct DOM
    const targetContainer = detachedWindow ? detachedWindow.document.body : container;

    const handleSliderChange = (event: Event, newValue: number | number[]) => {
        const value = Array.isArray(newValue) ? newValue[0] : newValue;
        const roundedValue = (currencyType === 'tournament_chips') ? Math.floor(value) : parseFloat(value.toFixed(2));
        setBuyInAmount(roundedValue);
    };

    const handleQuickAmountClick = (amount: number) => {
        const roundedValue = (currencyType === 'tournament_chips') ? Math.floor(amount) : parseFloat(amount.toFixed(2));
        setBuyInAmount(roundedValue);
    };

    return (
        <ThemeProvider theme={darkTheme}>
            <StyledDialog open={open} onClose={onClose} container={targetContainer}>
                <DialogContent sx={{ p: 3, pt: 4 }}>
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Stack>
                                <Typography className={styles.roomTitle}>
                                    {roomName || 'N/A'}
                                </Typography>
                                <Typography className={styles.roomStakes}>
                                    <img src={stakesChipIcon} alt="PokerSolutions Chip Icon" style={{ marginRight: '10px' }} width={isMobile ? 16 : 24} />
                                    {type === 'aof' ? (
                                        `Ante: ${formatAmount(ante || 0)} | Buy In: ${formatAmount((ante || 0) * 20)}`
                                    ) : (
                                        `${formatAmount(smallBlind)} / ${formatAmount(bigBlind)}`
                                    )}
                                </Typography>
                            </Stack>
                            {!isMobile && <Typography className={styles.roomType}>
                                {type === 'texas' ? t('BuyInModal:holdemNL') : t('BuyInModal:allInOrFold')}
                            </Typography>}
                        </Stack>
                        <Stack
                            className={styles.funds}
                            direction='row'
                            justifyContent="center"
                            alignItems="center"
                            sx={{ my: isMobile ? 3 : 'clamp(12px, 3vh, 32px)' }}
                        >
                            <Typography className={styles.fundsText}>{t('BuyInModal:availableFunds')}</Typography>
                            <Stack direction="column" alignItems="center" sx={{ ml: 4 }}>
                                <Stack direction="row">
                                    <img src={fundsChipIcon} alt="PokerSolutions Chip Icon" style={{ marginLeft: isMobile ? '10px' : '15px', marginRight: isMobile ? '5px' : '10px' }} width={isMobile ? 16 : 28} />
                                    <Typography className={styles.fundsText}>
                                        <strong>{formatAmount(numericBalance, true)}</strong>
                                    </Typography>
                                </Stack>
                                {sitOptions?.currencyType === 'crypto' && sitOptions?.currency && sitOptions?.exchangeRate && (
                                    <Box
                                        sx={{
                                            backgroundColor: 'rgba(97, 218, 251, 0.05)',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            border: '1px solid rgba(97, 218, 251, 0.1)',
                                            mt: 2
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#8695a6',
                                                fontSize: '0.875rem',
                                                display: 'grid',
                                                gap: '8px',
                                                '& .label': {
                                                    color: '#646cff',
                                                    opacity: 0.8,
                                                    fontWeight: 500,
                                                    marginRight: '8px'
                                                },
                                                '& .value': {
                                                    color: '#fff',
                                                    opacity: 0.9
                                                }
                                            }}
                                        >
                                            <span>
                                                <span className="label">{t('BuyInModal:rate')}</span>
                                                <span className="value">1 {sitOptions.currency} = ${sitOptions.exchangeRate.toFixed(6)}</span>
                                            </span>
                                            <span>
                                                <span className="label">{t('BuyInModal:currencyBalance', { currency: sitOptions.currency })}</span>
                                                <span className="value">{(numericBalance / sitOptions.exchangeRate).toFixed(2)}</span>
                                            </span>
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Stack>

                        {rebuyOffer ? (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: isMobile ? '20px' : 'clamp(12px, 3vh, 20px)',
                                backgroundColor: 'rgba(20,21,26,98)',
                                borderRadius: '4px',
                                mb: 2
                            }}>
                                <Typography sx={{
                                    color: '#FFFFFF',
                                    fontSize: isMobile ? '16px' : '18px',
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    mb: 1
                                }}>
                                    {t('BuyInModal:rebuy.reenterQuestion', { chips: Math.floor(rebuyOffer.chips), price: formatRebuyPrice(rebuyOffer.price) })}
                                </Typography>
                                {/* Action buttons moved to footer for rebuy */}
                                <Typography variant="caption" sx={{ mt: 1, opacity: 0.8 }}>
                                    {t('BuyInModal:rebuy.expiresIn', { time: `${(Math.floor((rebuyCountdown ?? 0) / 60)).toString().padStart(2, '0')}:${(((rebuyCountdown ?? 0) % 60)).toString().padStart(2, '0')}` })}
                                </Typography>
                            </Box>
                        ) : addonOffer ? (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: isMobile ? '20px' : 'clamp(12px, 3vh, 20px)',
                                backgroundColor: 'rgba(20,21,26,98)',
                                borderRadius: '4px',
                                mb: 2
                            }}>
                                <Typography sx={{
                                    color: '#FFFFFF',
                                    fontSize: isMobile ? '16px' : '18px',
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    mb: 1
                                }}>
                                    {t('BuyInModal:addon.available', { chips: Math.floor(addonOffer.chips), price: formatRebuyPrice(addonOffer.price) })}
                                </Typography>
                                <Typography variant="caption" sx={{ mt: 1, opacity: 0.8 }}>
                                    {t('BuyInModal:addon.offerEndsIn', { time: `${(Math.floor((addonCountdown ?? 0) / 60)).toString().padStart(2, '0')}:${(((addonCountdown ?? 0) % 60)).toString().padStart(2, '0')}` })}
                                </Typography>
                            </Box>
                        ) : type === 'aof' ? (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: isMobile ? '20px' : 'clamp(12px, 3vh, 20px)',
                                backgroundColor: 'rgba(20,21,26,98)',
                                borderRadius: '4px',
                                mb: 2
                            }}>
                                <Typography sx={{
                                    color: '#FFFFFF',
                                    backgroundColor: '#2E2F34',
                                    borderRadius: '4px',
                                    fontSize: isMobile ? '16px' : '20px',
                                    fontWeight: 500,
                                    textAlign: 'center',
                                    padding: '8px 16px'
                                }}>
                                    {formatAmount(safeBuyInLimits.min)}
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Typography className={styles.label} sx={{ mb: isMobile ? 2 : 3 }}>
                                    {t('BuyInModal:selectAmount')}
                                </Typography>
                                <Stack>
                                    <Stack direction="row" spacing={1} justifyContent="space-between" useFlexGap sx={{ mb: 2 }} flexWrap="wrap">
                                        <QuickAmountButton onClick={() => setBuyInAmount(safeBuyInLimits.min)}>
                                            <Stack>
                                                {t('BuyInModal:min')}
                                                <small>({formatAmount(safeBuyInLimits.min)})</small>
                                            </Stack>
                                        </QuickAmountButton>
                                        <QuickAmountButton
                                            onClick={() => setBuyInAmount(bigBlind * 30)}
                                            disabled={bigBlind * 30 < safeBuyInLimits.min || bigBlind * 30 > safeBuyInLimits.max}
                                        >
                                            30BB
                                        </QuickAmountButton>
                                        <QuickAmountButton
                                            onClick={() => setBuyInAmount(bigBlind * 50)}
                                            disabled={bigBlind * 50 < safeBuyInLimits.min || bigBlind * 50 > safeBuyInLimits.max}
                                        >
                                            50BB
                                        </QuickAmountButton>
                                        <QuickAmountButton
                                            onClick={() => setBuyInAmount(bigBlind * 100)}
                                            disabled={bigBlind * 100 < safeBuyInLimits.min || bigBlind * 100 > safeBuyInLimits.max}
                                        >
                                            100BB
                                        </QuickAmountButton>
                                        <QuickAmountButton onClick={() => setBuyInAmount(safeBuyInLimits.max)}>
                                            <Stack>
                                                {t('BuyInModal:max')}
                                                <small>({formatAmount(safeBuyInLimits.max)})</small>
                                            </Stack>
                                        </QuickAmountButton>
                                    </Stack>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        width: '100%',
                                        padding: isMobile ? '20px 40px' : 'clamp(12px, 3vh, 20px)',
                                        boxSizing: 'border-box',
                                        backgroundColor: 'rgba(20,21,26,98)',
                                        borderRadius: '4px',
                                        flexDirection: isMobile ? 'column' : 'row',
                                        mb: 1
                                    }}>
                                        <Typography sx={{ minWidth: 100, color: '#FFFFFF', backgroundColor: '#2E2F34', borderRadius: '4px', fontSize: isMobile? '16px' : '20px', fontWeight: 500, textAlign: 'center', padding: '8px', marginRight: isMobile ? 0 : '20px' }}>
                                            {formatAmount(buyInAmount)}
                                        </Typography>
                                        <CustomSlider
                                            slots={{ thumb: CustomSliderThumbComponent }}
                                            value={buyInAmount}
                                            onChange={handleSliderChange}
                                            min={safeBuyInLimits.min}
                                            max={safeBuyInLimits.max}
                                            step={(currencyType === 'tournament_chips' ? 1 : 0.01)}
                                            marks={false}
                                            valueLabelDisplay="auto"
                                        />
                                    </Box>
                                </Stack>

                                {!gameState.balanceWarning && !rebuyOffer && gameState.waitForBB !== false && gameState.waitForBB !== true && (
                                    <FormGroup sx={{ mb: 2 }}>
                                        <FormControlLabel
                                            control={<Checkbox />}
                                            disabled={!gameState.requiresChoice}
                                            label={
                                                <CheckboxLabelWithTooltip
                                                    text={t('BuyInModal:waitForBB.label')}
                                                    tooltipText={!gameState.requiresChoice ?
                                                        t('BuyInModal:waitForBB.unavailable') :
                                                        t('BuyInModal:waitForBB.description')
                                                    }
                                                />
                                            }
                                        />
                                    </FormGroup>
                                )}
                            </>
                        )}

                        {!rebuyOffer && !addonOffer && renderInfoBox()}

                        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                            {rebuyOffer ? (
                                <>
                                    <Button
                                        onClick={handleRebuyDecline}
                                        variant="contained"
                                        color="secondary"
                                        disabled={rebuySubmitting}
                                        sx={{ flexGrow: 1, textTransform: "capitalize", fontSize: "16px", width: "50%" }}
                                    >
                                        {t('BuyInModal:rebuy.decline')}
                                    </Button>
                                    <Button
                                        onClick={handleRebuyAccept}
                                        variant="contained"
                                        color="primary"
                                        disabled={(rebuyCountdown ?? 0) <= 0 || rebuySubmitting}
                                        sx={{ flexGrow: 1, textTransform: "capitalize", fontSize: "16px", width: "50%" }}
                                    >
                                        {t('BuyInModal:rebuy.accept')}
                                    </Button>
                                </>
                            ) : addonOffer ? (
                                <>
                                    <Button
                                        onClick={handleAddonDecline}
                                        variant="contained"
                                        color="secondary"
                                        disabled={addonSubmitting}
                                        sx={{ flexGrow: 1, textTransform: "capitalize", fontSize: "16px", width: "50%" }}
                                    >
                                        {t('BuyInModal:addon.skip')}
                                    </Button>
                                    <Button
                                        onClick={handleAddonAccept}
                                        variant="contained"
                                        color="primary"
                                        disabled={(addonCountdown ?? 0) <= 0 || addonSubmitting}
                                        sx={{ flexGrow: 1, textTransform: "capitalize", fontSize: "16px", width: "50%" }}
                                    >
                                        {t('BuyInModal:addon.buyAddon')}
                                    </Button>
                                </>
                            ) : gameState.balanceWarning?.type === 'notEnoughBalance' ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    component="a"
                                    href=""
                                    title="Link to your website here"
                                    target="_blank"
                                    sx={{
                                        flexGrow: 1,
                                        textTransform: "capitalize",
                                        fontSize: "16px",
                                        width: "50%",
                                        textDecoration: "none"
                                    }}
                                >
                                    {t('BuyInModal:cashier')}
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        onClick={onClose}
                                        variant="contained"
                                        color="secondary"
                                        sx={{ flexGrow: 1, textTransform: "capitalize", fontSize: "16px", width: "50%" }}
                                    >
                                        {t('BuyInModal:cancel')}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        sx={{ flexGrow: 1, textTransform: "capitalize", fontSize: "16px", width: "50%" }}
                                        onClick={() => handleChoice('post')}
                                        disabled={buyInAmount === 0}
                                    >
                                        {t('BuyInModal:play')}
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </Box>
                </DialogContent>
            </StyledDialog>
        </ThemeProvider>
    );
};

export default BuyinModal;
